import { CloseIcon, Popover, SearchIcon, Typography } from "@codat/orchard-ui";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import IconButton from "components/IconButton";
import TextInput from "components/TextInput";

import styles from "./CompanySearch.module.scss";

interface CompanySearchProps {
  onChange: (value: string) => void;
  onDebouncedValueChange: (input: string) => void;
  value: string;
  className?: string;
  popoverText?: string;
}

const CompanySearch = ({
  onDebouncedValueChange,
  onChange,
  value,
  className,
  popoverText,
}: CompanySearchProps) => {
  const [showSearchInput, setShowSearchInput] = useState(!!value);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchPopover, setSearchPopover] = useState(false);

  const debounceSearchInputChange = useDebouncedCallback((value: string) => {
    if (!showSearchInput) return;
    onDebouncedValueChange(value.trim());
  }, 500);

  useEffect(() => {
    if (showSearchInput) {
      inputRef?.current?.focus();
    }
  }, [showSearchInput]);

  const openSearchInput = () => {
    setShowSearchInput(true);
    setSearchPopover(true);
  };

  const closeSearchInput = () => {
    setShowSearchInput(false);
    setSearchPopover(false);
  };

  useEffect(() => {
    debounceSearchInputChange(value);
  }, [value, debounceSearchInputChange]);

  const iconRight =
    value?.length > 0
      ? {
          icon: <CloseIcon />,
          label: "Clear search",
          onClick: () => onChange(""),
          className: styles.clearButton,
        }
      : undefined;

  return (
    <div className={classNames(styles.container, className)}>
      <Popover
        popoverClassName={styles.searchTooltip}
        isOpen={searchPopover}
        placement="bottom-start"
        onClose={() => {
          // ignore
        }}
        size="parent"
        disableOverlay
        data-testid="company-search-popover"
        content={
          <div className={styles.popover}>
            <Typography variant="heading_lg" as="h3">
              Search companies
            </Typography>
            <Typography variant="body_md" as="p">
              {popoverText ??
                "Find a company by its name, ID, connected platforms or the user who created it"}
            </Typography>
          </div>
        }
      >
        {/* Don't remove this wrapping div. Popover hijacks the ref on its child component, so the input ref will stop working */}
        <div>
          <TextInput
            label="Search companies"
            showLabel={false}
            id="companies-search-input"
            data-testid="companies-search-input"
            className={classNames(styles.searchInput, {
              [styles.hidden]: !showSearchInput,
            })}
            value={value}
            placeholder="Start typing..."
            iconLeft={{ icon: <SearchIcon /> }}
            iconRight={iconRight}
            ref={inputRef}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              if (event.target.value.trim() === "") {
                closeSearchInput();
              }
            }}
            onChange={(event) => {
              onChange(event.target.value);
              if (searchPopover) {
                setSearchPopover(false);
              }
            }}
            maxLength={300}
            inputName="Search"
          />
        </div>
      </Popover>
      <IconButton
        className={classNames(styles.searchIcon, {
          [styles.displayNone]: showSearchInput,
        })}
        onClick={openSearchInput}
        data-testid="companies-search-icon"
        label="Search"
        buttonName="Search"
      >
        <SearchIcon />
      </IconButton>
    </div>
  );
};

export default CompanySearch;
